import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faTrash,
  faGripVertical,
  faAngleRight,
  faAngleLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import ButtonComponent from "../../../../components/ButtonComponent";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import {
  useDeleteMenuMutation,
  useUpdateMenuListOrderMutation,
} from "../../../../redux/services/MenusService";
import Swal from "sweetalert2";
import { Button } from "react-bootstrap";
import "./index.css";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { useUpdateAvailabilityStatusMutation } from "../../../../redux/services/menuItemService";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { formatFirestoreTimestamp } from "../../../../utility/Utility";
import { setRowsPerPage } from "../../../../redux/menusSlice";
import TableLoadingComponent from "../../../../components/TableLoadingComponent";
import ViewButtonComponent from "../../../../components/ViewButtonComponent";
import { useFirebaseMenuList } from "../../../../hooks/useFirebaseMenu";

export interface MenuItem {
  foodAttributes?: any;
  allergenInformation?: any;
  itemId: string;
  itemType: string;
  displayName: string;
  tinyId?: string;
  price: number;
  imageUrl: string;
  overridePrice?: number;
}

export interface Menu {
  isAvailable: boolean | undefined;
  id: string;
  createdAt: {
    _nanoseconds: number;
    _seconds: number;
  };
  displayName: string;
  brand: string;
  dineType: string;
  servingTime: string;
  restaurantId: string;
  items: MenuItem[];
  status: string;
  orderClosingTime?:string;
  maxDaysToScheduleAhead?: number;
  supportedApps?: string[];
  displayOrder?: number;
}

const MenusTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const rowsPerPage = useSelector((state: any) => state.menus.rowsPerPage);
  const restaurantStatus = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.status
  );

  const restaurantId = selectedRestaurantId;

  const [menus, setMenus] = useState<Menu[]>([]);
  const [deletingMenuId, setDeletingMenuId] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [originalOrder, setOriginalOrder] = useState<Menu[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteMenu] = useDeleteMenuMutation();
  const [updateMenuListOrder, { isLoading: isSavingOrder }] = useUpdateMenuListOrderMutation();
  const { data: menuData, loading } = useFirebaseMenuList(restaurantId);
  const [updateAvailabilityStatus] = useUpdateAvailabilityStatusMutation();
  
  useEffect(() => {
    if (menuData?.menus) {
      setMenus(menuData.menus);
      setOriginalOrder(menuData.menus);
      setHasOrderChanged(false);
    }
  }, [menuData]);

  const handleAddNewMenu = () => {
    if (restaurantStatus === "Pending") {
      toast.warning(
        "Your restaurant is still pending. Pending Until it gets approved"
      );
    } else {
      navigate("/menu/menu-list/add-menu-list");
    }
  };

  const handleDelete = async (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#ddd",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeletingMenuId(id);
        try {
          await deleteMenu({ restaurantId, menuId: id }).unwrap();
          Swal.fire("Deleted!", "Your menu has been deleted.", "success");
          // trigger(selectedRestaurantId);
          setDeletingMenuId(null);
        } catch (error) {
          Swal.fire(
            "Error!",
            "An error occurred while deleting the menu.",
            "error"
          );
          setDeletingMenuId(null);
        }
      }
    });
  };

  const handleUpdateMenu = (menu: Menu) => {
    navigate("/menu/menu-list/menu-edit", { state: { menu } });
  };

  const handleToggleChange = async (menuId: string, status: boolean) => {
    try {
      await updateAvailabilityStatus({
        restaurantId,
        menuId,
        menuType: "Menu",
        status,
      }).unwrap();
      // trigger(restaurantId);
    } catch (error) {
      toast.error("Error updating availability status");
    }
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setRowsPerPage(newRowsPerPage));
    setCurrentPage(1);
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentMenus = menus.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(menus.length / rowsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const checkIfOrderChanged = (newMenus: Menu[]) => {
    if (newMenus.length !== originalOrder.length) return true;
    
    return newMenus.some((menu, index) => menu.id !== originalOrder[index].id);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const items = [...menus];
    const [draggedItem] = items.splice(draggedIndex, 1);
    items.splice(dropIndex, 0, draggedItem);
    
    setMenus(items);
    setHasOrderChanged(checkIfOrderChanged(items));
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSaveOrder = async () => {
    try {
      const menuIds = menus.map(menu => menu.id);
      await updateMenuListOrder({
        restaurantId: selectedRestaurantId,
        displayOrder: menuIds.map((id, index) => ({ menuId: id, orderNumber: index + 1 })),
      }).unwrap();
      
      toast.success("Menu order updated successfully");
      setOriginalOrder(menus);
      setHasOrderChanged(false);
      // trigger(selectedRestaurantId);
    } catch (error) {
      toast.error("Failed to update menu order");
    }
  };

  const handleCancelReorder = () => {
    setMenus(originalOrder);
    setHasOrderChanged(false);
  };

  return (
    <div>
      <BreadCrumbs hasSubMenus={true} />
      <div className="menus-container">
        <div className="d-flex justify-content-end w-100 gap-2">
          {hasOrderChanged && (
            <>
              <Button variant="secondary" onClick={handleCancelReorder}>
                Cancel
              </Button>
              <ButtonComponent
                titel={isSavingOrder ? "Saving..." : "Save Changes"}
                onClick={handleSaveOrder}
                isDisabled={isSavingOrder}
                icon={isSavingOrder ? <FontAwesomeIcon icon={faSpinner} spin /> : undefined}
              />
            </>
          )}
          <ButtonComponent titel="Add" onClick={handleAddNewMenu} />
        </div>
        <Card className="menus-card">
          <CardHeader className="menus-card-header">
            <h4>Menu List</h4>
          </CardHeader>
          <CardBody>
            {loading ? (
              <TableLoadingComponent numColumns={6}/>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-hover custom-menu-table">
                  <thead>
                    <tr>
                      <th style={{ width: '80px' }}>
                        <h6 className="fw-bold mb-0">Order</h6>
                      </th>
                      <th>
                        <h6 className="fw-bold mb-0">Name</h6>
                      </th>
                      <th>
                        <h6 className="fw-bold mb-0">Dine Type</h6>
                      </th>
                      <th>
                        <h6 className="fw-bold mb-0">Brand</h6>
                      </th>
                      <th>
                        <h6 className="fw-bold mb-0">Serving Time</h6>
                      </th>
                      <th>
                        <h6 className="fw-bold mb-0">Created Date</h6>
                      </th>
                      <th>
                        <h6 className="fw-bold mb-0">Availability</h6>
                      </th>
                      <th>
                        <h6 className="fw-bold mb-0">Action</h6>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentMenus.map((menu, index) => (
                      <tr
                        key={menu.id}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        className={draggedIndex === index ? 'dragging' : ''}
                      >
                        <td style={{ textAlign: 'center' }}>
                          <FontAwesomeIcon
                            icon={faGripVertical}
                            color="#999"
                            size="lg"
                          />
                        </td>
                        <td>{menu.displayName}</td>
                        <td>{menu.dineType}</td>
                        <td>{menu.brand}</td>
                        <td>{menu.servingTime}</td>
                        <td>{formatFirestoreTimestamp(menu.createdAt)}</td>
                        <td>
                          <ToggleSwitch
                            initialChecked={menu.isAvailable}
                            menuId={menu.id}
                            onChange={handleToggleChange}
                          />
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <ViewButtonComponent onClick={() => handleUpdateMenu(menu)}/>
                            {deletingMenuId === menu.id ? (
                              <Button variant="light" disabled={true}>
                                <FontAwesomeIcon icon={faSpinner} spin />
                              </Button>
                            ) : (
                              <Button variant="light" onClick={() => handleDelete(menu.id)}>
                                <FontAwesomeIcon icon={faTrash} title="Delete" color="#F24E1E" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && menus.length > 0 && (
              <div className="d-flex justify-content-end align-items-center mt-3 px-3 py-2" style={{ gap: '20px' }}>
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                  <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>Rows per page:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ 
                      width: '70px', 
                      fontSize: '0.875rem',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      padding: '4px 24px 4px 8px'
                    }}
                    value={rowsPerPage}
                    onChange={(e) => handleChangeRowsPerPage(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                  <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                    {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, menus.length)} of {menus.length}
                  </span>
                  <div className="d-flex" style={{ gap: '4px' }}>
                    <button
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        color: currentPage === 1 ? '#ccc' : '#6c757d',
                        fontSize: '1rem',
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      <span style={{ lineHeight: 1 }}>|</span>
                      <FontAwesomeIcon icon={faAngleLeft}/>
                    </button>
                    <button
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        color: currentPage === 1 ? '#ccc' : '#6c757d',
                        fontSize: '1rem',
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <FontAwesomeIcon icon={faAngleLeft}/>
                    </button>
                    <button
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        color: currentPage === totalPages ? '#ccc' : '#6c757d',
                        fontSize: '1rem',
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                     <FontAwesomeIcon icon={faAngleRight}/>
                    </button>
                    <button
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        color: currentPage === totalPages ? '#ccc' : '#6c757d',
                        fontSize: '1rem',
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                      }}
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <FontAwesomeIcon icon={faAngleRight}/>
                      <span style={{ lineHeight: 1 }}>|</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default MenusTable;
